'use client'

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth0 } from '@auth0/auth0-react'
import { ToggleGroup, ToggleGroupItem } from '@alphabait/ui' // toggle-group'
import { DateRangePicker } from '@alphabait/ui' // date-range-picker'
import { DateRange } from 'react-day-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui' // select'
import { useTsrClient } from '../../end-users/tsr'
import { KeyMetrics } from './key-metrics'
import { CampaignQuestions } from './campaign-questions'
import { RiskFactors } from './risk-factors'
import { TopIncentives } from './top-incentives'
import { NavLink, useSearchParams } from 'react-router-dom'
import { EngagementRate } from './engagement-rate'
import {
  useDashboardLoadMonitor,
  measurePageLoad,
} from '@/lib/performance-monitoring'

// Helper to infer a preset from a DateRange value (tolerant to small time drift)
function matchPreset(v?: DateRange): '30d' | '90d' | '1y' | undefined {
  if (!v?.from || !v?.to) return undefined
  const to = new Date(v.to)
  const from = new Date(v.from)
  const ms = to.getTime() - from.getTime()
  const day = 24 * 60 * 60 * 1000
  const days = Math.round(ms / day)

  // Allow +/- 1 day tolerance
  if (Math.abs(days - 30) <= 1) return '30d'
  if (Math.abs(days - 90) <= 1) return '90d'
  if (days >= 363 && days <= 367) return '1y'
  return undefined
}

export default function AdvancedDashboard() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  // Performance monitoring
  const { markComplete } = useDashboardLoadMonitor()

  // Measure page load once
  useEffect(() => {
    measurePageLoad('Advanced Dashboard')
  }, [])

  // Helper to parse date from URL param (YYYY-MM-DD format)
  const parseDateParam = (param: string | null): Date | undefined => {
    if (!param) return undefined
    const date = new Date(param)
    return isNaN(date.getTime()) ? undefined : date
  }

  // Initialize state from URL params or defaults
  const getInitialDateRange = (): DateRange | undefined => {
    const fromParam = searchParams.get('from')
    const toParam = searchParams.get('to')

    if (fromParam && toParam) {
      const from = parseDateParam(fromParam)
      const to = parseDateParam(toParam)
      if (from && to) {
        return { from, to }
      }
    }

    // Default to last 30 days
    const now = new Date()
    const initialFrom = new Date()
    initialFrom.setDate(now.getDate() - 30)
    return { from: initialFrom, to: now }
  }

  const getInitialPreset = (): '30d' | '90d' | '1y' | undefined => {
    const fromParam = searchParams.get('from')
    const toParam = searchParams.get('to')

    if (fromParam && toParam) {
      const from = parseDateParam(fromParam)
      const to = parseDateParam(toParam)
      if (from && to) {
        return matchPreset({ from, to })
      }
    }

    return '30d' // Default preset
  }

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getInitialDateRange,
  )
  const [selectedPreset, setSelectedPreset] = useState<
    '30d' | '90d' | '1y' | undefined
  >(getInitialPreset)
  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(() => searchParams.get('customer') || undefined)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<
    string[] | undefined
  >(() => {
    const campaignParam = searchParams.get('campaign')
    return campaignParam ? [campaignParam] : undefined
  })
  const [selectedCampaignGroupId, setSelectedCampaignGroupId] = useState<
    string | undefined
  >(() => searchParams.get('group') || undefined)
  const [questionContext, setQuestionContext] = useState<
    'all' | 'standard' | 'referral'
  >(() => {
    const contextParam = searchParams.get('questionContext')
    if (contextParam === 'standard' || contextParam === 'referral') {
      return contextParam
    }
    return 'all'
  })
  const tsr = useTsrClient()

  // Handler for when a campaign is selected from child components
  const handleCampaignSelect = (campaignId: string) => {
    setSelectedCampaignIds([campaignId])
    setSelectedCampaignGroupId(undefined)
  }

  // Track when we're restoring from popstate to avoid pushing new history
  const isRestoringFromHistory = useRef(false)

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Mark that we're restoring from history
      isRestoringFromHistory.current = true

      // Read from current URL after popstate
      const params = new URLSearchParams(window.location.search)
      const campaignParam = params.get('campaign')
      const groupParam = params.get('group')
      const customerParam = params.get('customer')
      const fromParam = params.get('from')
      const toParam = params.get('to')

      // Update state from URL
      setSelectedCampaignIds(campaignParam ? [campaignParam] : undefined)
      setSelectedCampaignGroupId(groupParam || undefined)
      setSelectedCustomerId(customerParam || undefined)
      const questionContextParam = params.get('questionContext')
      if (
        questionContextParam === 'standard' ||
        questionContextParam === 'referral'
      ) {
        setQuestionContext(questionContextParam)
      } else {
        setQuestionContext('all')
      }

      // Parse and set date range
      if (fromParam && toParam) {
        const from = new Date(fromParam)
        const to = new Date(toParam)
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
          setDateRange({ from, to })
          setSelectedPreset(matchPreset({ from, to }))
        }
      } else {
        // Reset to default if no date in URL
        const now = new Date()
        const defaultFrom = new Date()
        defaultFrom.setDate(now.getDate() - 30)
        setDateRange({ from: defaultFrom, to: now })
        setSelectedPreset('30d')
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Sync state changes TO URL params (push to history)
  const isInitialMount = useRef(true)
  useEffect(() => {
    // Skip on initial mount to avoid pushing duplicate entry
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Skip if we're restoring from back/forward navigation
    if (isRestoringFromHistory.current) {
      isRestoringFromHistory.current = false
      return
    }

    const params = new URLSearchParams()

    const fromStr = dateRange?.from?.toISOString().split('T')[0]
    const toStr = dateRange?.to?.toISOString().split('T')[0]

    if (fromStr) params.set('from', fromStr)
    if (toStr) params.set('to', toStr)
    if (selectedCustomerId) params.set('customer', selectedCustomerId)
    if (selectedCampaignIds?.length === 1)
      params.set('campaign', selectedCampaignIds[0])
    if (selectedCampaignGroupId) params.set('group', selectedCampaignGroupId)
    if (questionContext !== 'all')
      params.set('questionContext', questionContext)

    // Use history.pushState to add to history without triggering react-router
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.pushState({}, '', newUrl)
  }, [
    dateRange,
    selectedCustomerId,
    selectedCampaignIds,
    selectedCampaignGroupId,
    questionContext,
  ])

  // Use ts-rest client for fetching customers
  // Note: Using 'dashboard-customers' key to avoid cache collision with CACHE_KEYS.CUSTOMERS
  // which stores extracted array data, while this stores the full response object
  const { data: customersData, isPending: loadingCustomers } =
    tsr.dashboard.getCustomers.useQuery({
      queryKey: ['dashboard-customers'],
    })

  // Use ts-rest client for fetching campaign groups
  const { data: campaignGroupsData, isPending: loadingCampaignGroups } =
    tsr.dashboard.getCampaignGroups.useQuery({
      queryKey: ['dashboard-campaign-groups', selectedCustomerId],
      queryData: {
        query: {
          customerId: selectedCustomerId,
        },
      },
    })

  const customers = customersData?.body || []
  const campaignGroupsResponse = campaignGroupsData?.body || {
    campaignGroups: [],
    ungroupedCampaigns: [],
  }

  // Create campaign dropdown options
  const campaignOptions = [
    // Campaign groups (show group name, value is the group ID)
    ...campaignGroupsResponse.campaignGroups.map((group) => ({
      label: `📁 ${group.name}`,
      value: `group:${group.id}`, // Use group ID with prefix
      type: 'group' as const,
      groupId: group.id,
      campaignIds: group.campaignIds,
    })),
    // Individual campaigns (show campaign name, value is the campaign ID)
    ...campaignGroupsResponse.ungroupedCampaigns.map((campaign) => ({
      label: campaign.name,
      value: `campaign:${campaign.id}`, // Use campaign ID with prefix
      type: 'campaign' as const,
      campaignIds: [campaign.id],
    })),
  ]

  // Mark dashboard as loaded once all components are rendered
  useEffect(() => {
    const isLoaded = customersData && campaignGroupsData
    if (isLoaded) {
      markComplete()
    }
  }, [customersData, campaignGroupsData, markComplete])

  const { user } = useAuth0()

  // Compute guaranteed ISO strings for required API parameters
  const computedTo = dateRange?.to ?? new Date()
  const computedFrom = dateRange?.from
    ? new Date(dateRange.from)
    : (() => {
        const d = new Date(computedTo)
        d.setDate(d.getDate() - 30)
        return d
      })()
  const periodStartIso = new Date(computedFrom).toISOString()
  const periodEndIso = new Date(computedTo).toISOString()

  // Customers are now fetched via ts-rest client above

  return (
    <div className="p-10">
      <div className="flex justify-between items-center">
        <h1 className="font-bold text-2xl m-0">
          {t('dashboard.hello_user', { user: user?.nickname })}
        </h1>
        <NavLink to="/dashboard" className="text-primary hover:underline">
          {t('dashboard.view_campaign_dashboards')}
        </NavLink>
      </div>
      <div className="flex justify-between items-center gap-4 my-6">
        <div className="flex items-center gap-4">
          {/* Time Range Presets */}
          <ToggleGroup
            type="single"
            value={selectedPreset}
            onValueChange={(preset) => {
              if (!preset) return
              const p = preset as '30d' | '90d' | '1y'

              setSelectedPreset(p)

              setDateRange(() => {
                const to = new Date()
                const from = new Date()

                if (p === '30d') {
                  from.setDate(to.getDate() - 30)
                } else if (p === '90d') {
                  from.setDate(to.getDate() - 90)
                } else if (p === '1y') {
                  from.setFullYear(to.getFullYear() - 1)
                }

                const newRange = { from, to }

                return newRange
              })
            }}
            className="border rounded-full h-10 gap-1"
          >
            <ToggleGroupItem
              value="30d"
              aria-label="30d"
              className="rounded-l-full text-sm font-medium text-gray-400 hover:text-gray-100 data-[state=on]:bg-gray-800 data-[state=on]:text-gray-900 data-[state=on]:shadow-sm px-4 h-9"
            >
              {t('dashboard.last_month')}
            </ToggleGroupItem>

            <ToggleGroupItem
              value="90d"
              aria-label="90d"
              className="text-sm font-medium text-gray-400  hover:text-gray-100 data-[state=on]:bg-gray-800 data-[state=on]:text-gray-900 data-[state=on]:shadow-sm px-4 h-9"
            >
              {t('dashboard.last_quarter')}
            </ToggleGroupItem>
            <ToggleGroupItem
              value="1y"
              aria-label="1y"
              className="rounded-r-full text-sm font-medium text-gray-400 hover:text-gray-100 data-[state=on]:bg-gray-800 data-[state=on]:text-gray-900 data-[state=on]:shadow-sm px-4 h-9"
            >
              {t('dashboard.last_year')}
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="w-64">
            <DateRangePicker
              key={`${dateRange?.from?.toISOString()}-${dateRange?.to?.toISOString()}`}
              value={dateRange}
              setValue={(v) => {
                setDateRange(v)
                setSelectedPreset(matchPreset(v))
              }}
              className="rounded-full"
            />
          </div>

          {/* Customer Selection */}
          <div className="flex items-center gap-2">
            <Select
              value={selectedCustomerId || 'all'}
              onValueChange={(value) => {
                setSelectedCustomerId(value === 'all' ? undefined : value)
                setSelectedCampaignIds(undefined)
                setSelectedCampaignGroupId(undefined)
              }}
              disabled={loadingCustomers}
            >
              <SelectTrigger className="w-48 rounded-full">
                <SelectValue placeholder={t('dashboard.select_customer')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('dashboard.all_customers')}
                </SelectItem>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Campaign Selection */}
          <div className="flex items-center gap-2">
            <Select
              value={
                selectedCampaignGroupId
                  ? `group:${selectedCampaignGroupId}`
                  : selectedCampaignIds
                  ? `campaign:${selectedCampaignIds[0]}` // For single campaigns
                  : 'all'
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  setSelectedCampaignIds(undefined)
                  setSelectedCampaignGroupId(undefined)
                } else if (value.startsWith('group:')) {
                  const groupId = value.replace('group:', '')
                  setSelectedCampaignGroupId(groupId)
                  setSelectedCampaignIds(undefined)
                } else if (value.startsWith('campaign:')) {
                  const campaignId = value.replace('campaign:', '')
                  setSelectedCampaignIds([campaignId])
                  setSelectedCampaignGroupId(undefined)
                }
              }}
              disabled={loadingCampaignGroups}
            >
              <SelectTrigger className="w-48 rounded-full">
                <SelectValue placeholder={t('dashboard.select_campaigns')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('dashboard.all_campaigns')}
                </SelectItem>
                {campaignOptions.map((option, index) => (
                  <SelectItem key={index} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Question Context Filter */}
          <div className="flex items-center gap-2">
            <Select
              value={questionContext}
              onValueChange={(value: 'all' | 'standard' | 'referral') => {
                setQuestionContext(value)
              }}
            >
              <SelectTrigger className="w-40 rounded-full">
                <SelectValue placeholder={t('dashboard.question_context')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('dashboard.question_context_all')}
                </SelectItem>
                <SelectItem value="standard">
                  {t('dashboard.question_context_standard')}
                </SelectItem>
                <SelectItem value="referral">
                  {t('dashboard.question_context_referral')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <KeyMetrics
        periodStart={periodStartIso}
        periodEnd={periodEndIso}
        customerId={selectedCustomerId}
        campaignIds={selectedCampaignIds}
        campaignGroupId={selectedCampaignGroupId}
      />

      {/* Campaign Questions / Questionnaire Analytics */}
      <div className="my-6">
        <CampaignQuestions
          periodStart={periodStartIso}
          periodEnd={periodEndIso}
          customerId={selectedCustomerId}
          campaignIds={selectedCampaignIds}
          campaignGroupId={selectedCampaignGroupId}
          questionContext={questionContext}
          onCampaignSelect={handleCampaignSelect}
        />
      </div>

      {/* Risk Factors and Top Incentives Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 my-6">
        <RiskFactors
          periodStart={periodStartIso}
          periodEnd={periodEndIso}
          customerId={selectedCustomerId}
          campaignIds={selectedCampaignIds}
          campaignGroupId={selectedCampaignGroupId}
        />
        <TopIncentives
          periodStart={periodStartIso}
          periodEnd={periodEndIso}
          customerId={selectedCustomerId}
          campaignIds={selectedCampaignIds}
          campaignGroupId={selectedCampaignGroupId}
        />
      </div>

      {/* Engagement Rate */}
      <div className="my-6">
        <EngagementRate
          periodStart={periodStartIso}
          periodEnd={periodEndIso}
          customerId={selectedCustomerId}
          campaignIds={selectedCampaignIds}
          campaignGroupId={selectedCampaignGroupId}
        />
      </div>
    </div>
  )
}
